import { cn, createComponent } from '@lair-finance/util'
import { HTMLAttributes } from 'react'

export const AgreementContent = createComponent<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      className={cn('flex flex-col py-4 px-1 rounded-[8px] max-h-[344px] bg-greyscale-700 text-greyscale-300 typo-body-2', className)}
      ref={ref}
      {...props}
    >
      <div
        className={cn(
          'px-3 overflow-x-hidden overflow-y-auto scrollbar',
          '[&>p]:mb-6',
          '[&>b]:mb-1 [&>b]:block [&>b]:text-greyscale-100 [&>b]:typo-headline-2',
          '[&>ul]:list-disc [&>ul]:pl-[14px]',
        )}
      >
        <b>Terms of Use</b>
        <p>
          Last Updated: July 15, 2024
          <br />
          Welcome to Lair Finance. Please read these Terms of Use (the “Terms”) carefully because they govern your use of the website located at
          lari.fi (the “Site”) and the services accessible via the Site and corresponding application (“App”) offered by Lair Finance (“Lair Finance,”
          “we,” “us,” or “our”). To make these Terms easier to read, the Site, our services, and the App are collectively called the “Services.”
        </p>
        <b>1. Agreement to Terms</b>
        <p>
          By using our Services, you agree to be bound by these Terms. If you don’t agree to be bound by these Terms, do not use the Services. If you
          are accessing and using the Services on behalf of a company (such as your employer) or another legal entity, you represent and warrant that
          you have the authority to bind that entity to these Terms. In that case, “you” and “your” will refer to that entity.
        </p>
        <b>2. Changes to these Terms or the Services</b>
        <p>
          We may update the Terms from time to time at our sole discretion. If we do, we’ll let you know by posting the updated Terms on the Site and
          the App and may also send other communications. It’s important that you review the Terms whenever we update them or you use the Services. If
          you continue to use the Services after we have posted updated Terms, it means that you accept and agree to the changes. If you don’t agree
          to be bound by the changes, you may not use the Services anymore. Because our Services are evolving over time, we may change or discontinue
          all or any part of the Services at any time and without notice at our sole discretion.
        </p>
        <b>3. Who May Use the Services?</b>
        <p>
          You may use the Services only if you are 18 years or older and capable of forming a binding contract with Lair Finance, and not otherwise
          barred from using the Services under applicable law. You may not use the Services if you are a resident of, or currently residing in, a
          country against which the United States has issued export sanctions or other trade restrictions or otherwise subject to trade sanctions.
          These countries may include but are not limited to Iran, North Korea, Sudan, and Syria.
        </p>
        <b>4. About the Services</b>
        <p>The Services include liquid staking derivative services to enable users to stake digital assets and obtain staking rewards.</p>
        <b>5. Feedback</b>
        <p>
          We appreciate feedback, comments, ideas, proposals, and suggestions for improvements to the Services (“Feedback”). If you choose to submit
          Feedback, you agree that we are free to use it without any restriction or compensation to you.
        </p>
        <b>6. Fees and Taxes</b>
        <p>
          <strong>a. Fees:</strong> You may incur fees in connection with your use of the Services (or certain portions thereof) and you agree to pay
          such fees. You are responsible for any fees incurred from third parties in connection with your use of the Services.
          <br />
          <strong>b. Taxes:</strong> You acknowledge and agree that you may suffer adverse tax consequences as a result of purchasing, holding,
          exchanging, selling, staking, transferring, or otherwise using tokens in any way. You hereby represent that you have consulted with a tax
          adviser that you deem advisable in connection with any use of tokens, or that you have had the opportunity to obtain tax advice but have
          chosen not to do so. Neither Lair Finance nor any of its affiliates has provided you with any tax advice. You agree to be fully responsible
          for any taxes resulting from any purchase, holding, exchange, sale, staking, transfer, or other use of tokens.
        </p>
        <b>7. Rights and Terms for Apps</b>
        <p>
          <strong>a. App License:</strong> If you comply with these Terms, Lair Finance grants you a limited non-exclusive, non-transferable license,
          with no right to sublicense, to use the App on your personal computers, tablets, wearable devices, and/or other devices and to run the App
          solely for your own personal non-commercial purposes. Except as expressly permitted in these Terms, you may not: (i) copy, modify, or create
          derivative works based on the App; (ii) distribute, transfer, sublicense, lease, lend, or rent the App to any third party; (iii) reverse
          engineer, decompile, or disassemble the App (unless applicable law permits, despite this limitation); or (iv) make the functionality of the
          App available to multiple users through any means.
        </p>
        <b>8. General Prohibitions and Lair Finance’s Enforcement Rights</b>
        <p>You agree not to do any of the following:</p>
        <ul>
          <li>
            Use, display, mirror, or frame the Services or any individual element within the Services, Lair Finance’s name, any Lair Finance
            trademark, logo, or other proprietary information, or the layout and design of any page or form contained on a page, without Lair
            Finance’s express written consent;
          </li>
          <li>
            Access, tamper with, or use non-public areas of the Services, Lair Finance’s computer systems, or the technical delivery systems of Lair
            Finance’s providers;
          </li>
          <li>
            Attempt to probe, scan, or test the vulnerability of any Lair Finance system or network or breach any security or authentication measures;
          </li>
          <li>
            Avoid, bypass, remove, deactivate, impair, descramble, or otherwise circumvent any technological measure implemented by Lair Finance or
            any of Lair Finance’s providers or any other third party (including another user) to protect the Services;
          </li>
          <li>
            Attempt to access or search the Services or download content from the Services using any engine, software, tool, agent, device, or
            mechanism (including spiders, robots, crawlers, data mining tools, or the like) other than the software and/or search agents provided by
            Lair Finance or other generally available third-party web browsers;
          </li>
          <li>
            Send any unsolicited or unauthorized advertising, promotional materials, email, junk mail, spam, chain letters, or other forms of
            solicitation;
          </li>
          <li>
            Use any meta tags or other hidden text or metadata utilizing a Lair Finance trademark, logo URL, or product name without Lair Finance’s
            express written consent;
          </li>
          <li>
            Use the Services, or any portion thereof, for any commercial purpose or for the benefit of any third party or in any manner not permitted
            by these Terms;
          </li>
          <li>
            Forge any TCP/IP packet header or any part of the header information in any email or newsgroup posting, or in any way use the Services to
            send altered, deceptive, or false source-identifying information;
          </li>
          <li>Attempt to decipher, decompile, disassemble, or reverse engineer any of the software used to provide the Services;</li>
          <li>
            Interfere with, or attempt to interfere with, the access of any user, host, or network, including, without limitation, sending a virus,
            overloading, flooding, spamming, or mail-bombing the Services;
          </li>
          <li>
            Collect or store any personally identifiable information from the Services from other users of the Services without their express
            permission;
          </li>
          <li>Impersonate or misrepresent your affiliation with any person or entity;</li>
          <li>Reverse look-up, track, or seek to track any information of any other users or visitors of the Site or Services;</li>
          <li>
            Take any actions that impose an unreasonable or disproportionately large load on the infrastructure of systems or networks of the Site or
            Services, or the infrastructure of any systems or networks connected to the Site or Services;
          </li>
          <li>
            Use the Services for market manipulation (such as pump and dump schemes, wash trading, self-trading, front running, quote stuffing, and
            spoofing or layering, regardless of whether prohibited by law);
          </li>
          <li>
            Engage or assist in any activity that violates any law, statute, ordinance, regulation, or sanctions program, including but not limited to
            the U.S. Department of Treasury’s Office of Foreign Assets Control (“OFAC”), or that involves proceeds of any unlawful activity (including
            but not limited to money laundering, terrorist financing, or deliberately engaging in activities designed to adversely affect the
            performance of the Services);
          </li>
          <li>Engage in deceptive trading activities;</li>
          <li>Use the Services to participate in fundraising for a business, protocol, or platform;</li>
          <li>Fabricate in any way any transaction or process related thereto;</li>
          <li>
            Disguise or interfere in any way with the IP address of the computer you are using to access or use the Services or that otherwise
            prevents us from correctly identifying the IP address of the computer you are using to access the Services;
          </li>
          <li>Transmit, exchange, or otherwise support the direct or indirect proceeds of criminal or fraudulent activity;</li>
          <li>Violate any applicable law or regulation; or</li>
          <li>Encourage or enable any other individual to do any of the foregoing.</li>
        </ul>
        <p>
          Lair Finance is not obligated to monitor access to or use of the Services or to review or edit any content. However, we have the right to do
          so for the purpose of operating the Services, to ensure compliance with these Terms, and to comply with applicable law or other legal
          requirements. We reserve the right, but are not obligated, to remove or disable access to any content, at any time and without notice,
          including, but not limited to, if we, at our sole discretion, consider it objectionable or in violation of these Terms. We have the right to
          investigate violations of these Terms or conduct that affects the Services. We may also consult and cooperate with law enforcement
          authorities to prosecute users who violate the law.
        </p>
        <b>9. Certain Risks</b>
        <p>
          Each user acknowledges, agrees, consents to, and assumes the risks of the matters described in this section. This includes but is not
          limited to the inherent risks associated with digital and cryptographic systems, such as the risk of loss of your digital assets, the risk
          of security breaches, and the risk of financial loss due to volatility and changes in market conditions.
        </p>
        <b>10. No Warranty</b>
        <p>
          The Services are provided on an “as is” and “as available” basis. You acknowledge and agree that your access and use of the Services are at
          your own risk. There is no representation or warranty that access to the Services will be continuous, uninterrupted, timely, or secure; that
          the information contained in the Services will be accurate, reliable, complete, or current, or that the Services will be free from errors,
          defects, viruses, or other harmful elements. No advice, information, or statement made in connection with the Services should be treated as
          creating any warranty concerning the Services.
        </p>
        <b>11. Disclaimers</b>
        <p>
          You understand and agree that the Services are provided “as is” and “as available,” without warranty of any kind, either express or implied,
          including, but not limited to, the implied warranties of merchantability, fitness for a particular purpose, and non-infringement. We make no
          warranty that the Services will meet your requirements or be available on an uninterrupted, secure, or error-free basis. We make no warranty
          regarding the quality, accuracy, timeliness, truthfulness, completeness, or reliability of any information or content on the Services.
        </p>
        <p>
          Lair Finance does not represent or warrant that any content on the Services is accurate, complete, reliable, current, or error-free. We will
          not be liable for any loss of any kind from any action taken or taken in reliance on material or information contained on the Services.
          While Lair Finance attempts to make your access to and use of the Services and any content therein safe, Lair Finance cannot and does not
          represent or warrant that the Services, any content therein, or our servers are free of viruses or other harmful components. We cannot
          guarantee the security of any data that you disclose online. You accept the inherent security risks of providing information and dealing
          online over the Internet and will not hold us responsible for any breach of security.
        </p>
        <b>12. Intellectual Property Rights</b>
        <p>
          We own all intellectual property and other rights in the Services and their contents, including, but not limited to, software, text, images,
          trademarks, service marks, copyrights, patents, and designs. Unless expressly authorized by us, you may not copy, modify, adapt, rent,
          license, sell, publish, distribute, or otherwise permit any third party to access or use the Services or any of their contents. Accessing or
          using the Services does not constitute a grant to you of any proprietary intellectual property or other rights in the Services or their
          contents.
        </p>
        <p>
          You will retain ownership of all intellectual property and other rights in any information and materials you submit through the Services.
          However, by uploading such information or materials, you grant us a worldwide, royalty-free, irrevocable license to use, copy, distribute,
          publish, and send this data in any manner in accordance with applicable laws and regulations.
        </p>
        <p>
          You may choose to submit comments, bug reports, ideas, or other feedback about the Services, including, without limitation, about how to
          improve the Services (collectively, “Feedback”). By submitting any Feedback, you agree that we are free to use such Feedback at our
          discretion and without additional compensation to you, and to disclose such Feedback to third parties (whether on a non-confidential basis
          or otherwise). If necessary under applicable law, then you hereby grant us a perpetual, irrevocable, non-exclusive, transferable, worldwide
          license under all rights necessary for us to incorporate and use your Feedback for any purpose.
        </p>
        <p>
          If (i) you satisfy all of the eligibility requirements set forth in these Terms, and (ii) your access to and use of the Services complies
          with these Terms, you are hereby granted a single, personal, limited license to access and use the Services. This license is non-exclusive,
          non-transferable, and freely revocable by us at any time without notice or cause at our sole discretion. Use of the Services for any purpose
          not expressly permitted by these Terms is strictly prohibited.
        </p>
        <b>13. Indemnification</b>
        <p>
          You agree to hold harmless, release, defend, and indemnify us and our officers, directors, employees, contractors, agents, affiliates, and
          subsidiaries from and against all claims, damages, obligations, losses, liabilities, costs, and expenses arising from (a) your access to and
          use of the Services; (b) your violation of these Terms, the rights of any third party, or any other applicable law, rule, or regulation; and
          (c) any other party’s access and use of the Services with your assistance or using any device or account that you own or control.
        </p>
        <b>14. Limitation of Liability</b>
        <p>
          Under no circumstances shall we or any of our officers, directors, employees, contractors, agents, affiliates, or subsidiaries be liable to
          you for any indirect, punitive, incidental, special, consequential, or exemplary damages, including (but not limited to) damages for loss of
          profits, goodwill, use, data, or other intangible property, arising out of or relating to any access to or use of the Services, nor will we
          be responsible for any damage, loss, or injury resulting from hacking, tampering, or other unauthorized access to or use of the Services, or
          from any access to or use of any information obtained by any unauthorized access to or use of the Services. We assume no liability or
          responsibility for any: (a) errors, mistakes, or inaccuracies of content; (b) personal injury or property damage, of any nature whatsoever,
          resulting from any access to or use of the Services; (c) unauthorized access to or use of any secure server or database in our control or
          the use of any information or data stored therein; (d) interruption or cessation of function related to the Services; (e) bugs, viruses,
          trojan horses, or the like that may be transmitted to or through the Services; (f) errors or omissions in, or loss or damage incurred as a
          result of, the use of any content made available through the Services; and (g) the defamatory, offensive, or illegal conduct of any third
          party. Under no circumstances shall we or any of our officers, directors, employees, contractors, agents, affiliates, or subsidiaries be
          liable to you for any claims, proceedings, liabilities, obligations, damages, losses, or costs in an amount exceeding the greater of (i) the
          amount you paid to us in exchange for access to and use of the Services, or (ii) $100.00. This limitation of liability applies regardless of
          whether the alleged liability is based on contract, tort, negligence, strict liability, or any other basis, and even if we have been advised
          of the possibility of such liability. Some jurisdictions do not allow the exclusion of certain warranties or the limitation or exclusion of
          certain liabilities and damages. Accordingly, some of the disclaimers and limitations set forth in these Terms may not apply to you. This
          limitation of liability shall apply to the fullest extent permitted by law.
        </p>
        <b>15. Arbitration and Class Action Waiver</b>
        <p>
          <strong>a. Binding Arbitration:</strong> Except for disputes in which either party seeks to bring an individual action in small claims court
          or seeks injunctive or other equitable relief for the alleged unlawful use of copyrights, trademarks, trade names, logos, trade secrets, or
          patents, you and Lair Finance: (i) waive the right to have any and all disputes or claims arising from these Terms, your use or access to
          the Services or any other disputes with Lair Finance (collectively, “Disputes”) resolved in a court; and (ii) waive any right to a jury
          trial. Instead, you and Lair Finance agree to arbitrate Disputes that are not resolved informally (as described below) through binding
          arbitration (i.e., the referral of a Dispute to one or more persons charged with reviewing the Dispute and making a final and binding
          determination to resolve it) instead of having the Dispute decided by a judge or jury in court).
        </p>
        <p>
          <strong>b. No Class Arbitrations, Class Actions, or Representative Actions:</strong> You and Lair Finance agree that any dispute is personal
          to you and Lair Finance and that any such dispute will be resolved solely through individual arbitration and will not be brought as a class
          arbitration, class action, or any other type of representative proceeding. Neither party agrees to class arbitration or to an arbitration in
          which an individual attempts to resolve a dispute as a representative of another individual or group of individuals. Further, you and Lair
          Finance agree that a dispute cannot be brought as a class or other type of representative action, whether within or outside of arbitration,
          or on behalf of any other individual or group of individuals.
        </p>
        <p>
          <strong>c. Process:</strong> You and Lair Finance agree that each will notify the other, in writing, of any Dispute within thirty (30) days
          of when it arises so that the parties can attempt, in good faith, to resolve the Dispute informally. Notice to Lair Finance shall be
          provided by sending an email to <a href={'mailto:contact@lair.fi'}>contact@lair.fi</a>. Your notice must include (i) your name, postal
          address, and email address; (ii) a description of the nature or basis of the Dispute; and (iii) the specific action that you are seeking. If
          you and Lair Finance cannot resolve the Dispute within thirty (30) days of Lair Finance receiving the notice, either you or Lair Finance
          may, as appropriate pursuant to this Section 13, commence an arbitration proceeding. You and Lair Finance agree that any arbitration or
          claim must be commenced or filed within one (1) year after the Dispute arose; otherwise, you and Lair Finance agree that the claim is
          permanently barred (which means that you will no longer have the right to assert a claim regarding the Dispute).
        </p>
        <p>
          <strong>d. Choice of Law:</strong> These Terms are governed by and will be construed under the laws of the state of Delaware, without regard
          to principles of conflict of laws, govern the Terms and any Dispute between you and us. Any Dispute under these Terms shall be finally
          settled by binding arbitration. Any unresolved Dispute arising out of or in connection with these Terms shall be referred to and finally
          resolved by arbitration under the rules of the London Court of International Arbitration (LCIA), which rules are deemed to be incorporated
          by reference into this Section 13 to the extent they are consistent with it. Any dispute arising from or relating to the subject matter of
          these Terms shall be finally settled in London, United Kingdom, in English, in accordance with the LCIA Arbitration Rules. Unless we agree
          otherwise, the arbitrator may not consolidate your claims with those of any other party. Any judgment on the award rendered by the
          arbitrator may be entered in any court of competent jurisdiction, to the extent a court therein would be deemed to be a court of competent
          jurisdiction other than any court located in British Virgin Islands. You further agree that the Services shall be deemed to be based solely
          in the British Virgin Islands and that, although the Services may be available in other jurisdictions, their availability does not give rise
          to general or specific personal jurisdiction in any forum outside the British Virgin Islands.
        </p>
        <p>
          <strong>e. Authority of Arbitrator:</strong> As limited by these Terms and applicable arbitration rules, the arbitrator will have: (i) the
          exclusive authority and jurisdiction to make all procedural and substantive decisions regarding a Dispute; and (ii) the authority to grant
          any remedy that would otherwise be available in court. The arbitrator may only conduct an individual arbitration and may not consolidate
          more than one individual’s claims, preside over any type of class or representative proceeding, or preside over any proceeding involving
          more than one individual.
        </p>
        <b>16. Lair Rewards Program</b>
        <p>
          The Lair Rewards Program is an initiative designed to provide extra incentives for users who stake their assets with Lair Finance. To
          participate, you must agree to the terms of use and stake your assets with Lair Finance during the campaign period. Detailed information
          about the program, including participation guidelines and the rewards structure, can be found in our official documents and blog articles.
          You have until the end of the campaign period to claim your allocated rewards; any unclaimed rewards will be forfeited after the campaign
          ends. The campaign duration is flexible and may be adjusted based on various factors.
        </p>
        <b>17. Lair Points</b>
        <p>
          You may be able to acquire points through your use of the Services (“Points”). Any balance of Points shown in your account constitutes only
          a measurement of the extent of your limited license to Points. You do not own the Points. Points are not a substitute for currency or medium
          of exchange, and do not have an equivalent value in real currency. You are not allowed to transfer Points outside of the Services. You are
          not allowed to sublicense, trade, sell or attempt to sell Points for money or other consideration, or exchange Points for value of any kind
          outside of the Services. Any such transfer or attempted transfer is prohibited and may cause the Points to be voided. Points are not
          redeemable, refundable or eligible for any currency or anything else of value. We may offer certain functions of the Services to users that
          have accumulated certain Points, but the availability of such functions is subject to change at any time in our sole discretion. We grant
          you a non-exclusive, non-transferable, non-sublicensable, limited license and right to use the Points in accordance with these Terms of Use
          while we offer the Services and your account is in good standing.
        </p>
        <b>18. Miscellaneous</b>
        <p>
          <strong>a. Changes:</strong> We may amend any portion of these Terms at any time by posting the revised version of these Terms with an
          updated revision date. The changes will become effective and shall be deemed accepted by you, the first time you use or access the Services
          after the initial posting of the revised Terms and shall apply on a going-forward basis with respect to your use of the Services including
          any transactions initiated after the posting date. In the event that you do not agree with any such modification, your sole and exclusive
          remedy is to terminate your use of the Services.
        </p>
        <p>
          <strong>b. Entire Agreement:</strong> These Terms (and any additional terms, rules, and conditions of participation that may be posted on
          the website of Lair Finance) including the Privacy Policy constitute the entire agreement with respect to the Services and supersede any
          prior agreements, oral or written.
        </p>
        <p>
          <strong>c. Privacy Policy:</strong> The Privacy Policy describes the ways we collect, use, store and disclose your personal information. You
          agree to the collection, use, storage, and disclosure of your data in accordance with the Privacy Policy.
        </p>
        <p>
          <strong>d. Survival:</strong> Upon termination of these Terms for any reason, all rights and obligations of the parties that by their nature
          are continuing will survive such termination.
        </p>
        <p>
          If you have any questions, claims, complaints, or suggestions, please contact us at <a href={'mailto:contact@lair.fi'}>contact@lair.fi</a>.
        </p>
        <b>Privacy Policy</b>
        <p>Last Updated: July 15, 2024</p>
        <b>1. Introduction</b>
        <p>
          Welcome to Lair Finance ("Lair Finance," "we," "us," or "our"). We are committed to protecting your privacy and ensuring that your personal
          information is handled in a safe and responsible manner. This Privacy Policy explains how we collect, use, process, and share your personal
          information when you use our services, including our website and decentralized applications (collectively, the "Services"). It also
          describes your privacy rights and how you can exercise them.
        </p>
        <b>2. Scope of this Privacy Policy</b>
        <p>
          This Privacy Policy applies to personal information processed by us, including on our website and other online or offline offerings. This
          Privacy Policy does not apply to any personal information processed on behalf of our customers through their use of our Services (“Customer
          Data”). Customer Data is governed by our customers' respective privacy policies.
        </p>
        <b>3. Personal Information We Collect</b>
        <p>
          The categories of personal information we collect depend on how you interact with us and our Services, and the requirements of applicable
          law. We collect information that you provide directly, information we collect automatically, and information from other sources.
        </p>
        <b>A. Personal Information You Provide Directly</b>
        <ul>
          <li>
            <strong>Account Creation:</strong> Name, email address, digital wallet address.
          </li>
          <li>
            <strong>Online Transactional Information:</strong> Wallet address, transaction details.
          </li>
          <li>
            <strong>Communications:</strong> Email address, digital wallet address, or online handle when you request information or support.
          </li>
          <li>
            <strong>Surveys:</strong> Information collected if you participate in surveys.
          </li>
          <li>
            <strong>Interactive Features:</strong> Information you submit through interactive features (e.g., blockchain, messaging, forums).
          </li>
          <li>
            <strong>Events:</strong> Information collected when attending or hosting conferences and events.
          </li>
          <li>
            <strong>Job Applications:</strong> Information provided in job applications.
          </li>
        </ul>
        <b>B. Personal Information Collected Automatically</b>
        <ul>
          <li>
            <strong>Automatic Collection:</strong> IP address, user settings, MAC address, cookie identifiers, browser or device information, location
            information, and information about your use of our Services.
          </li>
          <li>
            <strong>Cookies and Other Technologies:</strong> We use cookies, pixel tags, and other technologies to automatically collect information.
          </li>
        </ul>
        <b>C. Personal Information Collected from Other Sources</b>
        <ul>
          <li>
            <strong>Third-Party Services:</strong> Information from third-party services (e.g., social networking sites) and blockchain analysis.
          </li>
        </ul>
        <b>4. How We Use Your Personal Information</b>
        <p>We use your personal information for various business purposes, including:</p>
        <b>A. Providing Our Services</b>
        <ul>
          <li>Managing your information and accounts.</li>
          <li>Facilitating transactions.</li>
          <li>Providing access to our Services.</li>
          <li>Customer or technical support.</li>
          <li>Processing job applications.</li>
          <li>Registering for events.</li>
        </ul>
        <b>B. Administrative Purposes</b>
        <ul>
          <li>Direct marketing and research.</li>
          <li>Network and information security.</li>
          <li>Fraud prevention.</li>
          <li>Measuring interest and engagement in our Services.</li>
          <li>Improving our Services.</li>
          <li>Enforcing our agreements and policies.</li>
          <li>Compliance with legal obligations.</li>
        </ul>
        <b>C. Marketing and Advertising</b>
        <ul>
          <li>Email campaigns and custom audiences advertising.</li>
        </ul>
        <b>D. Other Purposes</b>
        <ul>
          <li>With your consent.</li>
          <li>Creating de-identified and aggregated information.</li>
        </ul>
        <b>5. How We Disclose Your Personal Information</b>
        <p>We disclose your personal information to third parties for various business purposes, including:</p>
        <p>
          <strong>A. Service Providers:</strong> Third-party service providers that assist us with IT support, hosting, payment processing, and
          customer service.
          <br />
          <strong>B. Business Partners:</strong> Partners with whom we jointly offer products or services.
          <br />
          <strong>C. Affiliates:</strong> Lair Finance affiliates.
          <br />
          <strong>D. Other Users or Third Parties:</strong> Individuals and third parties with whom you interact through our Services.
          <br />
          <strong>E. Advertising Partners:</strong> Third-party advertising partners for interest-based advertising.
          <br />
          <strong>F. Legal and Safety Reasons:</strong> To comply with legal obligations or protect our rights and safety.
          <br />
          <strong>G. Business Transfers:</strong> In the event of a merger, acquisition, or other business transaction.
        </p>
        <b>6. Your Privacy Choices and Rights</b>
        <p>A. Privacy Choices</p>
        <ul>
          <li>
            <strong>Email Communications:</strong> Opt-out using the unsubscribe link in emails.
          </li>
          <li>
            <strong>Cookies and Personalized Advertising:</strong> Adjust your browser/device settings to manage cookies.
          </li>
        </ul>
        <p>B. Privacy Rights</p>
        <ul>
          <li>
            <strong>Access and Portability:</strong> Access or obtain a copy of your personal information.
          </li>
          <li>
            <strong>Correction:</strong> Request correction of inaccurate or incomplete data.
          </li>
          <li>
            <strong>Deletion:</strong> Request deletion of your personal information.
          </li>
          <li>
            <strong>Opt-Out:</strong> Opt-out of certain processing activities.
          </li>
          <li>
            <strong>Restriction or Objection:</strong> Request restriction or object to processing.
          </li>
          <li>
            <strong>Withdraw Consent:</strong> Withdraw your consent for future processing.
          </li>
          <li>
            <strong>Appeal:</strong> Appeal our decision regarding your privacy request.
          </li>
        </ul>
        <p>To exercise your rights, contact us at privacy@lairfinance.com. We will process requests in accordance with applicable laws.</p>
        <b>7. European Privacy Rights and GDPR</b>
        <p>
          If you are located in the European Economic Area (EEA), United Kingdom, or Switzerland, you have certain rights under the General Data
          Protection Regulation (GDPR) and other applicable data protection laws. These include:
        </p>
        <p>
          <strong>A. Legal Basis for Processing:</strong> We process your personal data based on one or more of the following legal grounds:
        </p>
        <ul>
          <li>Your consent</li>
          <li>Performance of a contract with you</li>
          <li>Our legitimate interests</li>
          <li>Compliance with legal obligations</li>
        </ul>
        <p>
          <strong>B. Additional Rights:</strong>
        </p>
        <ul>
          <li>
            <strong>Right to Object:</strong> You can object to our processing of your personal data based on our legitimate interests.
          </li>
          <li>
            <strong>Data Portability:</strong> You can request to receive your personal data in a structured, commonly used, and machine-readable
            format.
          </li>
          <li>
            <strong>Automated Decision-Making:</strong> You have the right not to be subject to a decision based solely on automated processing,
            including profiling, which produces legal effects or similarly significantly affects you.
          </li>
        </ul>
        <p>
          <strong>C. Cross-Border Transfers:</strong> For transfers of personal data from the EEA, UK, or Switzerland to countries not deemed to
          provide an adequate level of data protection, we implement appropriate safeguards, such as Standard Contractual Clauses approved by the
          European Commission.
          <br />
          <strong>D. Supervisory Authority:</strong> You have the right to lodge a complaint with a supervisory authority in your country of
          residence, place of work, or where an alleged infringement of data protection law occurred.
          <br />
          <strong>E. Data Protection Officer:</strong> You can contact our Data Protection Officer at [admin@lari.fi](mailto:admin@lari.fi) for any
          questions or concerns regarding your personal data.
          <br />
          To exercise these rights or for more information, please contact us using the details provided in the "Contact Us" section.
          <br />
          [Renumber the subsequent sections accordingly]
          <br />
          Would you like me to integrate this new section into the full privacy policy, or do you need any modifications to this addition?
        </p>
        <b>8. International Data Transfers</b>
        <p>
          We transfer, process, and store your personal information globally. We use appropriate safeguards, such as Standard Contractual Clauses, to
          ensure your information is protected.
        </p>
        <b>9. Data Retention</b>
        <p>
          We retain your personal information for as long as necessary to fulfill the purposes for which it was collected, comply with legal
          obligations, resolve disputes, and enforce our agreements.
        </p>
        <b>10. Children’s Privacy</b>
        <p>
          Our Services are not directed to children under 18. We do not knowingly collect personal information from children. If you believe a child
          has provided us with personal information, contact us to delete it.
        </p>
        <b>11. Changes to this Privacy Policy</b>
        <p>
          We may update this Privacy Policy from time to time. We will notify you of any material changes as required by law. Continued use of our
          Services constitutes acceptance of the updated Privacy Policy.
        </p>
        <b>12. Contact Us</b>
        <p>
          If you have any questions or concerns about our privacy practices, please contact us at:
          <br />
          Lair Finance(<a href={'mailto:contact@lair.fi'}>contact@lair.fi</a>)
        </p>
      </div>
    </div>
  ),
  'AgreementContent',
)
